import React from "react";
import {
  buildInvoiceBillCardDiff,
  type InvoiceBillCardDiffRow,
  type InvoiceBillCardKind,
} from "../utils/invoiceBillApprovalCardDisplay";

type Props = {
  kind: InvoiceBillCardKind;
  changeDetails: { original_json?: unknown; raw_json?: unknown } | null | undefined;
  /** Slightly stronger type scale for “My requests” */
  variant?: "default" | "emphasized";
};

function DiffLine({
  row,
  valueClass,
}: {
  row: InvoiceBillCardDiffRow;
  valueClass: string;
}) {
  if (
    !row.changed ||
    !row.previous ||
    row.previous === row.current ||
    row.previous === "—"
  ) {
    return <span className={valueClass}>{row.current}</span>;
  }
  return (
    <span className={`${valueClass} inline-flex flex-wrap items-baseline gap-x-1.5 gap-y-0`}>
      <span className="line-through text-subtext-color decoration-neutral-400">
        {row.previous}
      </span>
      <span className="text-default-font">{row.current}</span>
    </span>
  );
}

export const InvoiceBillApprovalCardSummary: React.FC<Props> = ({
  kind,
  changeDetails,
  variant = "default",
}) => {
  const diff = buildInvoiceBillCardDiff(kind, changeDetails);
  if (!diff) {
    return (
      <div className="w-full rounded-lg border border-dashed border-neutral-200 bg-neutral-50/50 px-3 py-2.5 text-caption font-caption text-subtext-color">
        Document details unavailable
      </div>
    );
  }

  const isInvoice = kind === "invoice";
  const dateLabel = isInvoice ? "Invoice date" : "Bill date";
  const partyLabel = isInvoice ? "Customer" : "Vendor";
  const refLabel = isInvoice ? "Invoice #" : "Bill #";

  const labelClass = "text-[11px] font-medium leading-tight text-subtext-color";
  const valueClass =
    variant === "emphasized"
      ? "text-body font-body text-default-font font-semibold leading-snug"
      : "text-[12px] font-body leading-snug text-default-font";

  const amountLabelClass = "text-[11px] font-medium text-subtext-color";
  const amountValueClass =
    variant === "emphasized"
      ? "text-body font-body font-semibold text-default-font"
      : "text-[12px] font-body text-default-font";

  return (
    <div className="flex w-full flex-col gap-2.5 rounded-lg border border-solid border-neutral-200/90 bg-neutral-50/50 px-3 py-2.5">
      <div className="grid w-full grid-cols-1 gap-x-5 gap-y-2 sm:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className={labelClass}>{dateLabel}</span>
          <DiffLine row={diff.date} valueClass={valueClass} />
        </div>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className={labelClass}>{refLabel}</span>
          <DiffLine row={diff.reference} valueClass={valueClass} />
        </div>
        <div className="flex min-w-0 flex-col gap-0.5 sm:col-span-2">
          <span className={labelClass}>{partyLabel}</span>
          <DiffLine row={diff.party} valueClass={valueClass} />
        </div>
      </div>
      <div className="flex flex-col gap-1.5 rounded-md border border-solid border-neutral-200/70 bg-default-background px-2.5 py-2">
        <span className={`${labelClass} mb-0.5`}>
          {isInvoice ? "Invoice amounts" : "Bill amounts"}
        </span>
        <div className="flex flex-col gap-1">
          <div className="flex w-full min-w-0 items-baseline justify-between gap-2">
            <span className={`${amountLabelClass} shrink-0`}>Sub total</span>
            <div className="min-w-0 text-right">
              <DiffLine row={diff.amounts.subTotal} valueClass={amountValueClass} />
            </div>
          </div>
          <div className="flex w-full min-w-0 items-baseline justify-between gap-2">
            <span className={`${amountLabelClass} shrink-0`}>Tax total</span>
            <div className="min-w-0 text-right">
              <DiffLine row={diff.amounts.taxTotal} valueClass={amountValueClass} />
            </div>
          </div>
          <div className="flex w-full min-w-0 items-baseline justify-between gap-2 border-t border-solid border-neutral-100 pt-1">
            <span
              className={`${amountLabelClass} shrink-0 font-semibold text-default-font`}
            >
              Total amount
            </span>
            <div className="min-w-0 text-right">
              <DiffLine row={diff.amounts.totalAmount} valueClass={amountValueClass} />
            </div>
          </div>
          {diff.amounts.reverseChargeNote ? (
            <span className="text-[11px] font-body leading-snug text-info-700">
              {diff.amounts.reverseChargeNote}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
};
